import { Box, Alert } from "@mui/material";
import PropTypes from "prop-types";
import { useState } from "react";
import CSVReader from "react-csv-reader";

import LookupTableConstants from "@/components/membership/ChooseFormulaModal/lookupForm/LookupUtils/LookupTableConstants";
import LookupTableUtils from "@/components/membership/ChooseFormulaModal/lookupForm/LookupUtils/LookupTableUtils";
import { useUpdateDataContext } from "@/contexts/UpdateDataContext";
import "@/components/membership/ChooseFormulaModal/lookupForm/CSVLoader/CSVUploader.css";

function CSVUploader({
  updateLookupColumns,
  permittedOutputs,
  updateFullTable,
}) {
  const [invalidCSV, setInvalidCSV] = useState(false);
  const { affiliateFileData } = useUpdateDataContext();
  const affiliateColumns = affiliateFileData.columns;
  const { arrayOverlap, getEmptyLookupColumnsArray, removeUnpermittedOutputs } =
    LookupTableUtils();

  const handleCloseAlert = () => {
    setInvalidCSV(false);
  };

  const validateCSV = (data) => {
    // if the last element is a trailing new line, remove it
    if (data.at(-1)[0] === "") {
      data.pop();
    }
    // are all rows the same length ?
    const firstLength = data[0].length;
    const anyOtherLengths = data.filter((item) => item.length !== firstLength);
    if (anyOtherLengths.length) {
      return false;
    }
    return true;
  };

  const uploadCSVHandler = (data) => {
    if (validateCSV(data)) {
      const firstRow = data[0].slice(0, -1);
      const shouldFirstRowBeHeader = arrayOverlap(firstRow, affiliateColumns);

      if (shouldFirstRowBeHeader) {
        const { blankLookupColumnValue } = LookupTableConstants;
        const csvColumns = firstRow.map((item) =>
          !affiliateColumns.includes(item) ? blankLookupColumnValue : item
        );
        updateLookupColumns(csvColumns);

        let csvValues = data.slice(1); // everything that's not the headers
        if (permittedOutputs.length) {
          csvValues = removeUnpermittedOutputs(csvValues, permittedOutputs);
        }
        updateFullTable(csvValues);

        if (csvColumns.includes(blankLookupColumnValue)) {
          // setDisableContinueButton is provided by the parent via prop;
          // declared in PropTypes below. ESLint's no-undef can't trace
          // PropTypes entries, so suppress the false positive here.
          // eslint-disable-next-line no-undef
          setDisableContinueButton(true);
        }
      } else {
        // no headers in this csv, everything is data
        // set empty columns
        updateLookupColumns(getEmptyLookupColumnsArray());
        if (permittedOutputs.length) {
          data = removeUnpermittedOutputs(data, permittedOutputs);
        }
        updateFullTable(data);
      }
    } else {
      setInvalidCSV(true);
    }
  };

  return (
    <Box
      data-testid="csv-reader"
      sx={{
        marginTop: "10px",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        width: "100%",
      }}
    >
      <CSVReader
        aria-label="upload csv"
        onError={(e) => setInvalidCSV(true)}
        onFileLoaded={(data, fileInfo, originalFile) =>
          uploadCSVHandler(data, fileInfo, originalFile)
        }
      />
      {invalidCSV && (
        <Alert
          severity="error"
          sx={{ marginTop: "10px", width: "fit-content" }}
          onClose={handleCloseAlert}
        >
          Invalid CSV format. Unable to import.
        </Alert>
      )}
    </Box>
  );
}

CSVUploader.propTypes = {
  setUploadedCSV: PropTypes.func,
  setDisableContinueButton: PropTypes.func,
  setErrorMessage: PropTypes.func,
  setCSVUploadError: PropTypes.func,
  permittedOutputs: PropTypes.array,
};

export default CSVUploader;
